import { Navigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import MatterService from '../AppSettings/services/MatterService';
import { isFeatureEnabled } from '../AppSettings/environmentConfig';
import { isPipelineMatter, pipelineRouteQueryKey } from '../Matters/MatterDetails/pipelineMatter';
import '../BidSheets/bidDetailShared.css';

/**
 * Guard for the `/projectDetails/:matterId/*` SIBLING routes — files, tasks, notes (gap review
 * 2.11, the decision P3 deferred and P4 never took). Decision, recorded: **redirect**. A
 * converted project has exactly one front door — MatterDetails — and the sibling screens
 * are legacy matter surfaces never designed for a pipeline project; letting a bookmark or
 * hand-typed URL bypass that door showed eng-flavoured screens against converted matters.
 * Adopting them into the pipeline experience stays open as deliberate future work; silently
 * serving them was the one option that is never right.
 *
 * Everything that is NOT a converted project renders exactly what it always rendered — the
 * wrapped legacy screen — including fetch errors and the flag-off world (MatterDetails' own
 * "unknown must equal the old behaviour" rule).
 *
 * The query key is SHARED with MatterDetails deliberately: both callers are the same
 * routing decision (same fetch, retry: false, terminal), so front-door → sibling navigation
 * renders from cache with no flash (the default staleTime still background-refetches — cheap,
 * not free). This is not the AUTH-N07 shared-key loop: that needed a second consumer with
 * retryOnMount flipping an errored query back to pending under a mounted gate — these two
 * live on disjoint routes and never observe while the other decides.
 */
interface SiblingRouteGuardProps {
  children: JSX.Element;
}

const SiblingRouteGuard = ({ children }: SiblingRouteGuardProps): JSX.Element => {
  const { matterId = '' } = useParams();

  const matter = useQuery({
    queryKey: pipelineRouteQueryKey(matterId),
    queryFn: () => MatterService.getMatterDetails(matterId),
    enabled: isFeatureEnabled('bids') && !!matterId,
    retry: false,
    // NOT option-identical with MatterDetails, deliberately: a focus refetch here can flip
    // isPipeline mid-visit and fire the Navigate under a user typing in createTasks after a
    // transient first-fetch failure — a routing decision may be wrong once, but it must not
    // yank a form out from under someone to correct itself (review).
    refetchOnWindowFocus: false,
  });

  if (!isFeatureEnabled('bids')) return children;

  if (matter.isLoading) {
    return (
      <div className="bid-detail-page">
        <div className="bid-detail-notfound">
          <h2>Loading project…</h2>
        </div>
      </div>
    );
  }

  if (isPipelineMatter(matter.data)) return <Navigate to={`/projectDetails/${matterId}`} replace />;

  return children;
};

export default SiblingRouteGuard;
